import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEye,
  faPencil,
  faEnvelope,
  faCalendar,
  faClock,
  faLocationDot,
  faPalette,
  faImage,
  faRotate,
} from '@fortawesome/free-solid-svg-icons';

import { ROUTES } from '@/constants';
import { Paginator, Spinner } from '@/components/ui';
import { useInvitationsPage, PRESENTATION_KEYS, CONTENT_LIST_PAGE_SIZE } from '@/api';
import { StatusBadge, ctxStr, fmtDate, fmtTime } from '@/components/ContentList';
import styles from './ItemsList.module.css';

const PAGE_SIZE = CONTENT_LIST_PAGE_SIZE;

// ── Component ────────────────────────────────────────────

export function InvitationsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setHeaderTitle, setHeaderSlot } = useOutletContext<{
    setHeaderTitle: (title: string) => void;
    setHeaderSlot: (slot: React.ReactNode) => void;
  }>();
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, isFetching, isError } = useInvitationsPage(currentPage);

  useEffect(() => {
    if (isError) {
      toast.error('Error loading invitations. Please try again.');
    }
  }, [isError]);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: PRESENTATION_KEYS.all('invitation') });
  }, [queryClient]);

  const totalPages = data ? Math.ceil(data.count / PAGE_SIZE) : 1;

  useEffect(() => {
    const countStr = data ? ` (${data.count})` : '';
    setHeaderTitle(`Invitations${countStr}`);
    setHeaderSlot(
      <div className={styles.headerSlot}>
        <button className={styles.refreshBtn} onClick={handleRefresh} title="Refresh">
          <FontAwesomeIcon icon={faRotate} spin={isFetching} />
        </button>
        <button
          className={styles.newBtnHeader}
          onClick={() => navigate(ROUTES.dashboardGenerateInvitation)}
        >
          <FontAwesomeIcon icon={faPlus} />
          <span className={styles.hideOnMobile}>Generate New</span>
        </button>
      </div>,
    );
    return () => {
      setHeaderTitle('');
      setHeaderSlot(null);
    };
  }, [setHeaderTitle, setHeaderSlot, data, navigate, isFetching, handleRefresh]);

  return (
    <div className={styles.page}>
      {/* ── List ── */}
      {isLoading ? (
        <div className={styles.loadingState}>
          <Spinner />
        </div>
      ) : data?.results.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <FontAwesomeIcon icon={faEnvelope} />
          </div>
          <p className={styles.emptyTitle}>No invitations yet</p>
          <p className={styles.emptyText}>Generate your first AI-powered event invitation.</p>
          <button
            className={styles.emptyBtn}
            onClick={() => navigate(ROUTES.dashboardGenerateInvitation)}
          >
            <FontAwesomeIcon icon={faPlus} /> Generate Invitation
          </button>
        </div>
      ) : (
        <div className={styles.cardList}>
          {data?.results.map((item) => {
            const thumbnail = item.invitation_text ?? item.generated_images?.[0] ?? null;
            const eventType = ctxStr(item, 'eventType');
            const date = ctxStr(item, 'eventDate');
            const time = ctxStr(item, 'eventTime');
            const location = ctxStr(item, 'location');
            const theme = ctxStr(item, 'theme');
            const primary = ctxStr(item, 'primaryColor') || '#0a0a0a';
            const secondary = ctxStr(item, 'secondaryColor') || '#D4AF37';
            const statusCls =
              (styles[`status_${item.status}` as keyof typeof styles] as string) ?? '';

            return (
              <div
                key={item.id}
                className={`${styles.card} ${statusCls}`}
                onClick={() =>
                  navigate(ROUTES.dashboardInvitationDetail.replace(':id', String(item.id)))
                }
              >
                {/* Thumbnail */}
                <div className={styles.invThumb}>
                  {thumbnail ? (
                    <img src={thumbnail} alt={item.name} className={styles.thumbImg} />
                  ) : (
                    <div
                      className={styles.thumbPlaceholder}
                      style={{
                        background: `linear-gradient(160deg, ${primary} 0%, ${secondary} 100%)`,
                      }}
                    >
                      <FontAwesomeIcon icon={faImage} />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className={styles.cardBody}>
                  <div className={styles.cardTop}>
                    <h3 className={styles.cardName}>{item.name}</h3>
                    <StatusBadge
                      status={item.status}
                      since={item.created}
                      styles={styles as Record<string, string>}
                    />
                  </div>

                  {(eventType || location) && (
                    <div className={styles.cardMeta}>
                      {eventType && <span className={styles.metaChip}>{eventType}</span>}
                      {location && (
                        <span className={styles.metaItem}>
                          <FontAwesomeIcon icon={faLocationDot} className={styles.metaIcon} />
                          {location}
                        </span>
                      )}
                    </div>
                  )}

                  {(date || time || theme) && (
                    <div className={styles.cardDetails}>
                      {date && (
                        <span className={styles.detailItem}>
                          <FontAwesomeIcon icon={faCalendar} className={styles.detailIcon} />
                          {fmtDate(date)}
                        </span>
                      )}
                      {time && (
                        <span className={styles.detailItem}>
                          <FontAwesomeIcon icon={faClock} className={styles.detailIcon} />
                          {fmtTime(time)}
                        </span>
                      )}
                      {theme && (
                        <span className={styles.detailItem}>
                          <FontAwesomeIcon icon={faPalette} className={styles.detailIcon} />
                          {theme}
                        </span>
                      )}
                    </div>
                  )}

                  <div className={styles.cardFooter}>
                    Created {fmtDate(item.created)}
                    {item.created_by_email && (
                      <span className={styles.cardCreatedBy}>by {item.created_by_email}</span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className={styles.cardActions} onClick={(e) => e.stopPropagation()}>
                  <button
                    className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
                    onClick={() =>
                      navigate(ROUTES.dashboardInvitationDetail.replace(':id', String(item.id)))
                    }
                  >
                    <FontAwesomeIcon icon={faEye} /> View
                  </button>
                  <button
                    className={styles.actionBtn}
                    onClick={() =>
                      navigate(ROUTES.dashboardInvitationEdit.replace(':id', String(item.id)))
                    }
                  >
                    <FontAwesomeIcon icon={faPencil} /> Edit
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className={styles.paginatorWrap}>
          <Paginator page={currentPage} numPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      )}
    </div>
  );
}
